import React from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { routeConstants } from 'routes';
import { PermissionGate } from 'screens/Security';
import { IEmployerState, IUserState } from 'store/types';
import { EmployeeRole } from 'types';
import HeaderMainAction from './HeaderMainAction';
import HeaderMenuButton from './HeaderMenuButton';
import HeaderProfileSwitcher from './HeaderProfileSwitcher';
import RemoteSessionHeader from './RemoteSessionHeader';

interface IHeaderProps {
  isLoading: boolean;
  hideNav?: boolean;
  hideMakePayment?: boolean;
  isNavOpen?: boolean;
  user?: IUserState;
  employers?: Record<string, IEmployerState>;
  onNavOpenChange(): void;
}

const Layout = styled.header.attrs({ role: 'banner' })<{ $remoteSession?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.header};
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(theme.header.height)};
  background-color: ${theme.color.neutral[900]};

  ${({ $remoteSession }) =>
    $remoteSession &&
    css`
      top: ${toRem(20)};

      @media (min-width: ${theme.breakpoint.med}px) {
        top: 0;
      }
    `}
`;

const ProfileSwitcher = styled.section`
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
`;

const HeaderActions = styled.section`
  display: flex;
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  padding-right: ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.med}px) {
    display: flex;
    padding-right: ${toRem(theme.space['3xl'])};
  }
`;

const Header: React.FC<IHeaderProps> = ({ user, employers, hideNav, hideMakePayment, isNavOpen, onNavOpenChange, isLoading = false }) => (
  <Layout data-cy="app-layout-header" $remoteSession={!!user?.remoteSession}>
    {user?.remoteSession && <RemoteSessionHeader />}
    <ProfileSwitcher>
      <HeaderMenuButton isNavOpen={isNavOpen} onClick={onNavOpenChange} isHidden={hideNav} />
      <HeaderProfileSwitcher user={user} employers={employers} isLoading={isLoading} onNavOpenChange={onNavOpenChange} isNavOpen={isNavOpen} />
    </ProfileSwitcher>

    {!hideMakePayment && (
      <HeaderActions>
        <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN, EmployeeRole.MANAGER]}>
          <HeaderMainAction data-cy="make-payment-button" label="Make Payment" to={routeConstants.private.makePayment} />
        </PermissionGate>
      </HeaderActions>
    )}
  </Layout>
);

export default Header;
